import axios from 'axios'
import { API_URL } from './config'

// Types for semantic branding data
export type BrandDocument = {
  id: string
  step: 'inicial' | 'concorrentes' | 'atributos' | 'analise' | 'concluido'
  marca: string
  url: string
  visibility: 'private' | 'public'
  ultimaAnalise: string | null
  createdAt: string
  updatedAt: string
  metadata?: {
    concorrentesSugeridos?: Array<{
      marca: string
      url: string
      descricao: string
      grau_de_concorrencia: number
    }>
    concorrentesSelecionados?: Array<{
      marca: string
      url: string
      descricao: string
    }>
    atributosSugeridos?: Array<string>
    atributosSelecionados?: Array<string>
    analiseComparativa?: {
      scores: Record<string, Record<string, number>>
      scoreGeral: Record<string, number>
      insights: string
      sugestoes?: string[]
      pontosFortes?: string[]
      oportunidades?: string[]
      geradoEm: string
    }
  }
}

export type BrandDocumentsResponse = {
  brandDocuments: BrandDocument[]
  pagination: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

export type Competitor = {
  marca: string
  url: string
  descricao?: string
  grau_de_concorrencia?: number
  grau_concorrencia?: number
  'grau de concorrencia'?: number
}

export type Attribute = string

// Semantic Branding services
export const semanticBrandingService = {
  // List all brand documents for the authenticated user
  listBrandDocuments: async (
    page: number = 1,
    limit: number = 10,
  ): Promise<BrandDocumentsResponse> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.get(
      `${API_URL}/api/semantic-branding?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Get a specific brand document by ID
  getBrandDocument: async (
    id: string,
  ): Promise<{ brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.get(`${API_URL}/api/semantic-branding/${id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  },
  // Create a new brand document
  createBrandDocument: async (
    marca: string,
    url: string,
    sugerirConcorrentes: boolean = false,
  ): Promise<{ message: string; brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.post(
      `${API_URL}/api/semantic-branding`,
      {
        marca,
        url,
        sugerirConcorrentes,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Update brand and URL (only in initial step)
  updateBrandInfo: async (
    id: string,
    marca: string,
    url: string,
  ): Promise<{ message: string; brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.patch(
      `${API_URL}/api/semantic-branding/${id}`,
      {
        marca,
        url,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Suggest competitors with AI
  suggestCompetitors: async (
    id: string,
  ): Promise<{
    message: string
    brandDocument: BrandDocument
    concorrentes: Competitor[]
    usage: {
      inputTokens: number
      outputTokens: number
      totalCostUsd: string
    }
  }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.post(
      `${API_URL}/api/semantic-branding/${id}/concorrentes`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Save selected competitors
  saveCompetitors: async (
    id: string,
    competitors: Competitor[],
  ): Promise<{ message: string; brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.patch(
      `${API_URL}/api/semantic-branding/${id}/concorrentes`,
      {
        concorrentesSelecionados: competitors,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Delete all competitors
  deleteAllCompetitors: async (
    id: string,
  ): Promise<{ message: string; brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.delete(
      `${API_URL}/api/semantic-branding/${id}/concorrentes/all`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Suggest attributes with AI
  suggestAttributes: async (
    id: string,
  ): Promise<{
    message: string
    brandDocument: BrandDocument
    atributos: string[]
    usage: {
      inputTokens: number
      outputTokens: number
      totalCostUsd: string
    }
  }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.post(
      `${API_URL}/api/semantic-branding/${id}/atributos`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Save selected attributes
  saveAttributes: async (
    id: string,
    attributes: string[],
  ): Promise<{ message: string; brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.patch(
      `${API_URL}/api/semantic-branding/${id}/atributos`,
      {
        atributosSelecionados: attributes,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Delete all attributes
  deleteAllAttributes: async (
    id: string,
  ): Promise<{ message: string; brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.delete(
      `${API_URL}/api/semantic-branding/${id}/atributos/all`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Run comparative analysis
  runAnalysis: async (
    id: string,
    mostrarSugestoes: boolean = true,
  ): Promise<{
    message: string
    brandDocument: BrandDocument
    isReanalise: boolean
    usage?: {
      inputTokens: number
      outputTokens: number
      totalCostUsd: string
    }
  }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.post(
      `${API_URL}/api/semantic-branding/${id}/analise?mostrarSugestoes=${mostrarSugestoes}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Update document visibility
  updateVisibility: async (
    id: string,
    visibility: 'public' | 'private',
  ): Promise<{ message: string; brandDocument: BrandDocument }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.patch(
      `${API_URL}/api/semantic-branding/${id}/visibility`,
      {
        visibility,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Delete brand document
  deleteBrandDocument: async (
    id: string,
  ): Promise<{
    message: string
    documentoId: string
    marca: string
    deletadoEm: string
  }> => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.delete(
      `${API_URL}/api/semantic-branding/${id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  },
  // Get a public brand document by ID (no auth required)
  getPublicBrandDocument: async (
    id: string,
  ): Promise<{ brandDocument: BrandDocument }> => {
    const response = await axios.get(
      `${API_URL}/api/semantic-branding/${id}/public`,
    )
    return response.data
  },
}
